import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import TCN from './tokens';
import Avatar from './Avatar';
import Icon from './Icon';
import LanguageMenu from './LanguageMenu';

/**
 * Tappable avatar that opens an iOS-style bottom sheet with a Sign out action.
 * Replaces the standalone <Avatar/> in TCN mobile TopBar leading slots.
 */
const AccountAvatar = ({ name, size = 36, ring = true, online = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleSignOut = async () => {
    setOpen(false);
    await supabase.auth.signOut();
    navigate('/login-authentication', { replace: true });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('tcn.nav.account', 'Account')}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          lineHeight: 0,
        }}
      >
        <Avatar name={name} size={size} ring={ring} online={online} />
      </button>

      {open && createPortal(
        <div
          onClick={() => setOpen(false)}
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            animation: 'tcnFade 160ms ease-out',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t('tcn.nav.account', 'Account')}
            style={{
              width: '100%',
              maxWidth: 480,
              background: TCN.surface1,
              borderTop: `1px solid ${TCN.border}`,
              borderRadius: '20px 20px 0 0',
              padding: 16,
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
              fontFamily: TCN.fontSans,
              animation: 'tcnSlideUp 220ms cubic-bezier(0.32, 0.72, 0, 1)',
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 2,
                background: TCN.border,
                margin: '0 auto 16px',
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '4px 4px 16px',
              }}
            >
              <Avatar name={name} size={48} ring />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: TCN.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {name}
                </div>
                <div style={{ fontSize: 12, color: TCN.textDim }}>
                  {t('tcn.role.agent', 'Agent')}
                </div>
              </div>
            </div>

            {/* Language switcher — gated by tenant.enabled_languages */}
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  color: TCN.textMute,
                  padding: '0 4px 8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 700,
                }}
              >
                {t('tcn.account.language', 'Language')}
              </div>
              <LanguageMenu onAfter={() => setOpen(false)} />
            </div>

            <button
              type="button"
              onClick={handleSignOut}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                background: TCN.surface2,
                color: TCN.red,
                border: `1px solid ${TCN.border}`,
                borderRadius: 12,
                fontFamily: TCN.fontSans,
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              <span>{t('tcn.nav.signOut', 'Sign out')}</span>
              <Icon name="arrow-right" size={18} />
            </button>

            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                width: '100%',
                marginTop: 8,
                padding: '14px 16px',
                background: 'transparent',
                color: TCN.textDim,
                border: 'none',
                borderRadius: 12,
                fontFamily: TCN.fontSans,
                fontWeight: 600,
                fontSize: 15,
                cursor: 'pointer',
              }}
            >
              {t('tcn.nav.cancel', 'Cancel')}
            </button>
          </div>
          <style>{`
            @keyframes tcnFade { from { opacity: 0 } to { opacity: 1 } }
            @keyframes tcnSlideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
          `}</style>
        </div>,
        document.body
      )}
    </>
  );
};

export default AccountAvatar;
